// Auto Tone — stretches the histogram to cover the full 0..255 range
// per channel independently. This is the Photoshop "Auto Tone" algorithm.
//
// Algorithm:
//   1. For each channel (R, G, B), compute its histogram
//   2. Find the 0.1% and 99.9% percentiles (clip 0.1% from each end)
//   3. Stretch the channel so that the 0.1% → 0 and 99.9% → 255
//   4. Clamp to 0..255

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface AutoToneParams {
  /** Fraction of pixels to clip from each end (default 0.001 = 0.1%) */
  clip: number;
}

export const AutoTone: Filter<AutoToneParams> = {
  id: "auto-tone",
  displayName: "Auto Tone",
  defaultParams: { clip: 0.001 },

  apply(input: PixelBuffer, params: AutoToneParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    // Build per-channel histograms
    const histR = new Uint32Array(256);
    const histG = new Uint32Array(256);
    const histB = new Uint32Array(256);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o]!;
      const g = src[o + 1]!;
      const b = src[o + 2]!;
      histR[r] = (histR[r] ?? 0) + 1;
      histG[g] = (histG[g] ?? 0) + 1;
      histB[b] = (histB[b] ?? 0) + 1;
    }

    const total = n;
    const clipFrac = clampRange(params.clip, 0, 0.5);
    const threshold = Math.round(total * clipFrac);

    // Find the min/max for each channel after clipping
    const minR = findPercentile(histR, threshold);
    const maxR = findPercentile(histR, total - threshold);
    const minG = findPercentile(histG, threshold);
    const maxG = findPercentile(histG, total - threshold);
    const minB = findPercentile(histB, threshold);
    const maxB = findPercentile(histB, total - threshold);

    // Build lookup tables
    const lutR = buildStretchLut(minR, maxR);
    const lutG = buildStretchLut(minG, maxG);
    const lutB = buildStretchLut(minB, maxB);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = lutR[src[o]!]!;
      dst[o + 1] = lutG[src[o + 1]!]!;
      dst[o + 2] = lutB[src[o + 2]!]!;
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};

function findPercentile(hist: Uint32Array, target: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) {
    sum += hist[i] ?? 0;
    if (sum >= target) return i;
  }
  return 255;
}

function buildStretchLut(minVal: number, maxVal: number): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  if (maxVal <= minVal) {
    lut.fill(0);
    return lut;
  }
  const range = maxVal - minVal;
  const scale = 255 / range;
  for (let v = 0; v < 256; v++) {
    if (v <= minVal) lut[v] = 0;
    else if (v >= maxVal) lut[v] = 255;
    else lut[v] = Math.round((v - minVal) * scale);
  }
  return lut;
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
